<template>
    <jet-form-section @submitted="submitForm">
        <template #title>Payout Details</template>
        <template #description>Choose your preferred payout method and provide the necessary details to receive your earnings. Ensure the information you enter is accurate to facilitate a smooth payout process. </template>
        <template #form>
            <!-- Payout Method Selection -->
            <div class="col-span-6 sm:col-span-4">
                <jet-label for="payoutMethod" value="Payout Method" />
                <select id="payoutMethod" v-model="form.payoutMethod" class="mt-2 block w-full rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6">
                    <!--<option value="ach">ACH Payout (Bank Account)</option>-->
                    <option value="cashApp">Cash App</option>
                    <option value="venmo">Venmo</option>
                </select>

                <jet-input-error :message="form.errors.payoutMethod" class="mt-2" />
            </div>

            <!-- ACH Payout Fields -->
            <template v-if="form.payoutMethod === 'ach'">
                <!-- Full Name Input -->
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="fullName" value="Full Name" />
                    <jet-input id="fullName" v-model="form.fullName" type="text" class="mt-1 block w-full" autocomplete="off" />
                    <jet-input-error :message="form.errors.fullName" class="mt-2" />
                </div>

                <!-- Routing Number Input -->
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="routingNumber" value="Routing Number" />
                    <jet-input id="routingNumber" v-model="form.routingNumber" type="text" class="mt-1 block w-full" autocomplete="off" />
                    <jet-input-error :message="form.errors.routingNumber" class="mt-2" />
                </div>

                <!-- Account Number Input -->
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="accountNumber" value="Account Number" />
                    <jet-input id="accountNumber" v-model="form.accountNumber" type="text" class="mt-1 block w-full" autocomplete="off" />
                    <jet-input-error :message="form.errors.accountNumber" class="mt-2" />
                </div>

                <!-- Confirm Account Number Input -->
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="confirmAccountNumber" value="Confirm Account Number" />
                    <jet-input id="confirmAccountNumber" v-model="form.confirmAccountNumber" type="text" class="mt-1 block w-full" autocomplete="off" />
                    <jet-input-error :message="form.errors.confirmAccountNumber" class="mt-2" />
                </div>
            </template>

            <!-- Cash App Specific Fields -->
            <template v-else-if="form.payoutMethod === 'cashApp'">
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="cashApp" value="Cash App" />
                    <jet-input id="cashApp" v-model="form.cashApp" type="text" class="mt-1 block w-full" placeholder="$Cashtag" autocomplete="off" />
                    <jet-input-error :message="form.errors.cashApp" class="mt-2" />
                </div>
            </template>

            <!-- Venmo Specific Fields -->
            <template v-else-if="form.payoutMethod === 'venmo'">
                <div class="col-span-6 sm:col-span-4">
                    <jet-label for="venmo" value="Venmo" />
                    <jet-input id="venmo" v-model="form.venmo" type="text" class="mt-1 block w-full" placeholder="@Username" autocomplete="off" />
                    <jet-input-error :message="form.errors.venmo" class="mt-2" />
                </div>
            </template>
        </template>
        <template #actions>
            <jet-action-message :show="form.recentlySuccessful" class="mr-3">
                <div class="flex items-center space-x-1">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Saved</span>
                </div>
            </jet-action-message>
            <jet-button :disabled="form.processing" :class="{ 'opacity-25': form.processing }">Save</jet-button>
        </template>
    </jet-form-section>
</template>

<script>
export default {
    components: {
        JetFormSection: require("@/Jetstream/FormSection").default,
        JetButton: require("@/Jetstream/Button").default,
        JetActionMessage: require("@/Jetstream/ActionMessage").default,
        JetInput: require("@/Jetstream/Input").default,
        JetLabel: require("@/Jetstream/Label").default,
        JetInputError: require("@/Jetstream/InputError").default,
    },
    data() {
        return {
            form: this.$inertia.form({
                _method: "POST",
                payoutMethod: "cashApp",
                fullName: "John Doe",
                routingNumber: "110000000",
                accountNumber: "000123456789",
                confirmAccountNumber: "000123456789",
                cashApp: "",
                venmo: "",
                country: "US",
            }),
        }
    },
    computed: {
        isAccountNumberConfirmed() {
            return this.form.payoutMethod === "ach" && this.form.accountNumber && this.form.accountNumber === this.form.confirmAccountNumber
        },
    },
    methods: {
        isValidCashAppTag(tag) {
            return /^\$[a-zA-Z0-9_]+$/.test(tag)
        },
        isValidVenmoUsername(username) {
            return /^@[a-zA-Z0-9_.]+$/.test(username)
        },
        submitForm() {
            this.form.clearErrors()
            // Adjusted to check payout method specific validation before submission
            if (this.form.payoutMethod === "ach" && !this.isAccountNumberConfirmed) {
                this.form.errors.confirmAccountNumber = "Account numbers do not match."
                return
            }
            if (this.form.payoutMethod === "cashApp" && !this.isValidCashAppTag(this.form.cashApp)) {
                this.form.errors.cashApp = "Invalid Cash App $Cashtag."
                return
            }
            if (this.form.payoutMethod === "venmo" && !this.isValidVenmoUsername(this.form.venmo)) {
                this.form.errors.venmo = "Invalid Venmo @Username."
                return
            }
            this.form.processing = true
            this.form
                .transform((data) => ({
                    ...data,
                    routingNumber: data.routingNumber,
                    accountNumber: data.accountNumber,
                    confirmAccountNumber: data.confirmAccountNumber,
                }))
                .post(route("user.payout.update"), {
                    errorBag: "updateBankAccount",
                    preserveScroll: true,
                })
        },
    },
}
</script>
